import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { brandedThemeStyle } from '@/lib/brand-color';
import { loadPinState, DEFAULT_PIN_SETTINGS, LAUNCH_HINT_KEY, type PinSettings } from '@/lib/pinLock';
import { PinScreen } from './PinScreen';

/**
 * Global app-lock. Mounted once near the app root (a fixed overlay sibling, not
 * a wrapper) so it never remounts on route changes. When the lock is enabled and
 * "locked", it covers everything with the branded PIN screen.
 *
 * When it locks is user-configurable (profiles PIN columns, loaded via
 * loadPinState):
 *   - lockOnLaunch  — cover a cold start (fresh tab / PWA launch).
 *   - lockOnResume  — re-lock on returning to the foreground after being hidden
 *                     ≥ resumeAfterSeconds.
 *   - lockOnIdle    — lock after idleMinutes of no interaction in-app.
 *
 * localStorage hints (`nk-applock-on` + `nk-applock-launch`) let a cold start
 * cover instantly — before the async profile lookup — without flashing the CRM.
 * The business logo/colour is cached (`nk-applock-brand`) so the lock is branded
 * from the very first frame.
 */

const HINT_KEY = 'nk-applock-on';
const SESSION_UNLOCKED = 'nk-applock-unlocked';
const BRAND_KEY = 'nk-applock-brand';

interface Brand { logoUrl: string | null; name: string | null; primaryColor: string | null }

function hintedLock(): boolean {
    try {
        return localStorage.getItem(HINT_KEY) === '1'
            && localStorage.getItem(LAUNCH_HINT_KEY) !== '0'
            && sessionStorage.getItem(SESSION_UNLOCKED) !== '1';
    } catch { return false; }
}

function cachedBrand(): Brand | null {
    try { const raw = localStorage.getItem(BRAND_KEY); return raw ? JSON.parse(raw) as Brand : null; } catch { return null; }
}

async function resolveBrand(uid: string): Promise<Brand | null> {
    const { data: owned } = await supabase
        .from('businesses').select('logo_url, primary_color, business_name').eq('owner_id', uid).maybeSingle();
    if (owned) {
        const b = owned as { logo_url: string | null; primary_color: string | null; business_name: string | null };
        return { logoUrl: b.logo_url, name: b.business_name, primaryColor: b.primary_color };
    }
    const { data: mem } = await supabase
        .from('business_members')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .select('businesses(logo_url, primary_color, business_name)' as any)
        .eq('user_id', uid).eq('status', 'active').limit(1).maybeSingle();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const biz = (mem as any)?.businesses;
    if (biz) return { logoUrl: biz.logo_url ?? null, name: biz.business_name ?? null, primaryColor: biz.primary_color ?? null };
    return null;
}

export function PinLockOverlay() {
    const [resolved, setResolved] = useState(false);
    const [authed, setAuthed] = useState(false);
    const [userId, setUserId] = useState<string | null>(null);
    const [enabled, setEnabled] = useState(false);
    const [length, setLength] = useState(4);
    const [profile, setProfile] = useState<{ full_name?: string | null; avatar_url?: string | null } | null>(null);
    const [brand, setBrand] = useState<Brand | null>(cachedBrand());
    const [settings, setSettings] = useState<PinSettings>(DEFAULT_PIN_SETTINGS);
    const [locked, setLocked] = useState<boolean>(hintedLock());
    const hiddenAtRef = useRef<number | null>(null);
    const enabledRef = useRef(false);
    const settingsRef = useRef<PinSettings>(DEFAULT_PIN_SETTINGS);
    settingsRef.current = settings;

    const doLock = () => {
        try { sessionStorage.removeItem(SESSION_UNLOCKED); } catch { /* noop */ }
        setLocked(true);
    };

    useEffect(() => {
        let active = true;
        const resolve = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!active) return;
            const uid = session?.user?.id ?? null;
            setAuthed(!!uid);
            setUserId(uid);
            if (!uid) {
                setEnabled(false); enabledRef.current = false; setLocked(false);
                try { localStorage.removeItem(HINT_KEY); } catch { /* noop */ }
                setResolved(true);
                return;
            }
            const st = await loadPinState(uid);
            if (!active) return;
            setEnabled(st.enabled); enabledRef.current = st.enabled; setLength(st.length);
            setSettings({
                lockOnLaunch: st.lockOnLaunch, lockOnResume: st.lockOnResume,
                resumeAfterSeconds: st.resumeAfterSeconds, lockOnIdle: st.lockOnIdle, idleMinutes: st.idleMinutes,
            });
            try {
                localStorage.setItem(HINT_KEY, st.enabled ? '1' : '0');
                localStorage.setItem(LAUNCH_HINT_KEY, st.lockOnLaunch ? '1' : '0');
            } catch { /* noop */ }
            if (st.enabled) {
                const { data } = await supabase.from('profiles').select('full_name, avatar_url').eq('id', uid).maybeSingle();
                if (active) setProfile(data ?? null);
                void resolveBrand(uid).then((b) => {
                    if (!active || !b) return;
                    setBrand(b);
                    try { localStorage.setItem(BRAND_KEY, JSON.stringify(b)); } catch { /* noop */ }
                });
                let unlockedThisSession = false;
                try { unlockedThisSession = sessionStorage.getItem(SESSION_UNLOCKED) === '1'; } catch { /* noop */ }
                // Cold start (not yet unlocked this session) → lock only if the
                // user wants a launch lock.
                setLocked(!unlockedThisSession && st.lockOnLaunch);
            } else {
                setLocked(false);
            }
            setResolved(true);
        };
        void resolve();

        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if (event === 'SIGNED_OUT') {
                setAuthed(false); setUserId(null); setEnabled(false); enabledRef.current = false; setLocked(false);
            } else if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
                if (session?.user?.id) void resolve();
            }
        });
        return () => { active = false; subscription.unsubscribe(); };
    }, []);

    // Re-lock when the app returns to the foreground after being hidden a while.
    useEffect(() => {
        const onVis = () => {
            if (document.visibilityState === 'hidden') { hiddenAtRef.current = Date.now(); return; }
            const hiddenMs = hiddenAtRef.current ? Date.now() - hiddenAtRef.current : 0;
            hiddenAtRef.current = null;
            const s = settingsRef.current;
            if (enabledRef.current && s.lockOnResume && hiddenMs >= s.resumeAfterSeconds * 1000) doLock();
        };
        document.addEventListener('visibilitychange', onVis);
        return () => document.removeEventListener('visibilitychange', onVis);
    }, []);

    // Idle auto-lock: while unlocked + enabled, lock after N minutes of no input.
    useEffect(() => {
        if (!authed || !enabled || locked || !settings.lockOnIdle) return;
        let timer = 0;
        const ms = Math.max(1, settings.idleMinutes) * 60_000;
        const reset = () => { window.clearTimeout(timer); timer = window.setTimeout(doLock, ms); };
        const events = ['pointerdown', 'keydown', 'touchstart', 'scroll'] as const;
        events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
        reset();
        return () => {
            window.clearTimeout(timer);
            events.forEach((e) => window.removeEventListener(e, reset));
        };
    }, [authed, enabled, locked, settings.lockOnIdle, settings.idleMinutes]);

    const onUnlock = () => {
        try { sessionStorage.setItem(SESSION_UNLOCKED, '1'); } catch { /* noop */ }
        setLocked(false);
    };

    if (authed && enabled && locked && userId) {
        return (
            <PinScreen
                userId={userId}
                length={length}
                displayName={profile?.full_name}
                avatarUrl={profile?.avatar_url}
                businessLogoUrl={brand?.logoUrl ?? null}
                businessName={brand?.name ?? null}
                primaryColor={brand?.primaryColor ?? null}
                onUnlock={onUnlock}
            />
        );
    }
    // Instant cold-start cover (hint says locked) until the async resolve lands —
    // prevents the app from flashing behind the lock. Branded if we have a cache.
    if (!resolved && locked) {
        const b = brand;
        return (
            <div
                className="fixed inset-0 z-[200] pointer-events-auto bg-slate-950 flex items-center justify-center"
                style={brandedThemeStyle(b?.primaryColor ?? undefined)}
            >
                {b?.logoUrl && (
                    <div className="rounded-full bg-gradient-to-b from-primary/80 to-primary/30 p-[2px] shadow-xl">
                        <div className="flex items-center justify-center rounded-full bg-white px-5 py-3 ring-1 ring-black/5">
                            <img src={b.logoUrl} alt="" className="h-9 max-w-[160px] rounded-lg object-contain" />
                        </div>
                    </div>
                )}
            </div>
        );
    }
    return null;
}
